import clsx from 'clsx'
import {useEffect, useState} from 'react'

import Progress from '../Progress'
import {interfaceLanguage} from '~/services/intl'
import {showErrorPopup} from '~/services/popup'
import {getDemoVoices, pauseAudio, playAudio, resumeAudio, subscribeOnEvents, synthesizeDemo} from '~/services/synthesis'
import {useObjectUrl} from '~/utils/useObjectUrl'

import type {SoundStatus} from '~/services/synthesis'

type Props = {
    text: string
    active: boolean
    onActivate: () => void
}

export default function SynthesizeButton({text, active, onActivate}: Props) {
    const [ready, setReady] = useState(true)
    const [voice, setVoice] = useState('')
    const [soundStatus, setSoundStatus] = useState<SoundStatus>('stopped')
    const [objectUrl, setBlob] = useObjectUrl()

    useEffect(() => subscribeOnEvents({
        ended: () => setSoundStatus('stopped'),
        play: () => active
            ? setSoundStatus('playing')
            : setSoundStatus('stopped'),
        pause: () => active
            ? setSoundStatus(objectUrl ? 'paused' : 'stopped')
            : setSoundStatus('stopped'),
    }), [objectUrl, active, text])

    useEffect(() => {
        getDemoVoices()
            .then(voices => {
                const voice = voices.find(({languages}) => languages[0] == interfaceLanguage) ||
                    voices.find(({languages}) => languages.includes(interfaceLanguage)) ||
                    voices[0]

                setVoice(voice.id)
            })
            .catch(showErrorPopup)
    }, [])

    useEffect(() => {
        if (objectUrl)
            playAudio(objectUrl)
        else
            setSoundStatus('stopped')

        return pauseAudio
    }, [objectUrl])

    const handleClick = () => {
        onActivate()

        switch (soundStatus) {
        case 'stopped':
            return objectUrl ? playAudio(objectUrl) : play()
        case 'playing':
            return pauseAudio()
        case 'paused':
            return resumeAudio()
        }
    }

    const play = () => {
        pauseAudio()
        setReady(false)

        synthesizeDemo(
            voice,
            text,
            1,
            'auto',
            'neutral',
        )
            .then(setBlob)
            .catch(showErrorPopup)
            .finally(() => setReady(true))
    }

    if (!ready)
        return <Progress className='code__button code__button_spinner'/>

    return <button
        type='button'
        disabled={!text || !voice}
        className={clsx('pi code__button code__button_play', soundStatus == 'playing' ? 'pi-pause' : 'pi-play')}
        onClick={handleClick}
    />
}
